import React from "react";
import { Trophy, Mic, Medal, Award, Gavel, Star, GraduationCap, Lightbulb, Users } from "lucide-react";
import { HONORS } from "../lib/content";

const RANK_STYLE = {
  "1st Place": { bg: "#F5BB1F", color: "#1C201E", icon: Trophy },
  "2nd Place": { bg: "#5C8470", color: "#FAF8F5", icon: Medal },
  "3rd Place": { bg: "#9C7510", color: "#FAF8F5", icon: Medal },
  "Speaker":   { bg: "#1C201E", color: "#FAF8F5", icon: Mic },
  "Judge":     { bg: "#5C8470", color: "#FAF8F5", icon: Gavel },
  "Innovation": { bg: "#2A2F2C", color: "#F5BB1F", icon: Lightbulb },
  "Featured":  { bg: "#1C201E", color: "#F5BB1F", icon: Star },
  "Organizer": { bg: "#5C8470", color: "#FAF8F5", icon: Users },
  "Mentor":    { bg: "#5C8470", color: "#FAF8F5", icon: GraduationCap },
};

const LEVEL_STYLE = {
  "Global":        "bg-ink text-alabaster",
  "International": "bg-emerald2 text-alabaster",
  "Arab World":    "bg-terracotta text-ink",
  "Regional":      "bg-terracotta/80 text-ink",
  "National":      "bg-alabaster-surface text-ink border border-alabaster-line",
  "School":        "bg-alabaster-surface text-ink border border-alabaster-line",
};

const EARLIER_HONORS = [
  { icon: Gavel, title: "Intel ISEF — Jury Panel Chairman", meta: "Judging", tag: "National" },
  { icon: Award, title: "Hult Prize — Judge", meta: "2019 & 2020", tag: "Global" },
  { icon: Star, title: "Stars of Science — S11 Contestant", meta: "Featured", tag: "Pan-Arab" },
  { icon: GraduationCap, title: "Intel ISEF — Mentee", meta: "1st & 2nd Place · World", tag: "Global" },
];

const getCategory = (h) => {
  if (["1st Place", "2nd Place", "3rd Place"].includes(h.rank)) return "winning";
  if (h.rank === "Judge" || h.rank === "Organizer" || h.rank === "Mentor" || /mentor/i.test(h.title)) return "judging";
  return "workshop";
};

const GROUPS = [
  { key: "winning", label: "Winnings", blurb: "National & regional competition wins for the students and teams I mentor." },
  { key: "judging", label: "Judging & Mentoring", blurb: "Evaluating, mentoring and organising — guiding innovators and shaping competitions." },
  { key: "workshop", label: "Workshops", blurb: "Hands-on sessions for students, teachers and faculty across schools and universities." },
];

const HonorCard = ({ h, i, gi }) => {
  const rs = RANK_STYLE[h.rank] || RANK_STYLE["Speaker"];
  const Icon = rs.icon;
  return (
    <article
      data-testid={`honor-card-${gi}`}
      className="reveal group bg-white border border-alabaster-line rounded-3xl overflow-hidden hover:border-terracotta/50 hover:-translate-y-2 hover:shadow-[0_30px_60px_-30px_rgba(28,32,30,0.25)] transition-all duration-500"
      style={{ transitionDelay: `${i * 60}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-ink">
        <img
          src={h.image}
          alt={h.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-95 group-hover:opacity-100"
          style={h.imagePosition ? { objectPosition: h.imagePosition } : undefined}
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/15 to-transparent"
          aria-hidden
        />

        {/* Top-row: rank pill + year */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-display font-bold tracking-widest uppercase rounded-full px-3 py-1.5 shadow-lg"
            style={{ background: rs.bg, color: rs.color }}
          >
            <Icon size={12} strokeWidth={2.4} />
            {h.rank}
          </span>
          <span className="text-[10px] font-display font-bold tracking-widest uppercase text-alabaster bg-ink/60 backdrop-blur-sm rounded-full px-3 py-1.5">
            {h.year}
          </span>
        </div>

        {/* Bottom-row: level badge */}
        <div className="absolute left-4 bottom-4">
          <span
            className={`inline-block text-[10px] font-display font-bold tracking-widest uppercase rounded-full px-3 py-1 ${
              LEVEL_STYLE[h.level] || LEVEL_STYLE["Global"]
            }`}
          >
            {h.level}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-display text-lg font-bold text-ink leading-snug group-hover:text-terracotta transition-colors">
          {h.title}
        </h3>
        <p className="mt-3 text-sm text-ink-soft leading-relaxed">
          {h.body}
        </p>
      </div>
    </article>
  );
};

const Honors = () => (
  <section
    id="contests"
    data-testid="section-contests"
    className="relative py-16 sm:py-20 bg-alabaster overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 items-end">
        <div className="lg:col-span-7 reveal">
          <div className="eyebrow inline-flex items-center gap-3">
            <span className="block w-10 h-px bg-terracotta" />
            Honors & Recognition
          </div>
          <h2
            data-testid="contests-heading"
            className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-ink leading-[1.05] tracking-tight"
          >
            Winning. Judging. <br />
            <span className="text-terracotta">Mentoring world champions.</span>
          </h2>
        </div>
        <div className="lg:col-span-5 reveal">
          <p className="text-ink-soft text-base sm:text-lg leading-relaxed">
            From the Hult Prize jury and Intel ISEF mentorship to PSSF national wins and international workshops — recent recognition for me and the students I lead.
          </p>
        </div>
      </div>

      {GROUPS.map((g) => {
        const items = HONORS.filter((h) => getCategory(h) === g.key).sort((a, b) => Number(b.year) - Number(a.year));
        if (!items.length) return null;
        return (
          <div key={g.key} className="mt-16 first:mt-0" data-testid={`honors-group-${g.key}`}>
            <div className="reveal flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7">
              <h3 className="eyebrow inline-flex items-center gap-3 !text-ink">
                <span className="block w-8 h-px bg-terracotta" />
                {g.label}
                <span className="text-terracotta font-bold tracking-normal normal-case">{items.length}</span>
              </h3>
              <p className="text-ink-soft text-sm leading-relaxed sm:max-w-md sm:text-right">{g.blurb}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
              {items.map((h, idx) => (
                <HonorCard key={h.title} h={h} i={idx} gi={HONORS.indexOf(h)} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Earlier honors — compact cards */}
      <div className="mt-14">
        <div className="reveal eyebrow inline-flex items-center gap-3 mb-6">
          <Award size={16} className="text-terracotta" />
          Earlier Honors
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {EARLIER_HONORS.map((e, i) => {
            const Icon = e.icon;
            return (
              <article
                key={i}
                data-testid={`earlier-honor-card-${i}`}
                className="reveal group rounded-2xl p-5 flex flex-col gap-4 text-alabaster hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-24px_rgba(28,32,30,0.45)] transition-all duration-500"
                style={{ transitionDelay: `${i * 60}ms`, background: "#1C201E", border: "1px solid #2A2F2C" }}
              >
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-xl bg-terracotta text-ink flex items-center justify-center transition-transform group-hover:scale-105">
                    <Icon size={20} strokeWidth={2.1} />
                  </span>
                  <span className="text-[10px] font-display font-bold tracking-widest uppercase text-terracotta-soft border border-terracotta/40 rounded-full px-2.5 py-1">
                    {e.tag}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-base font-bold text-alabaster leading-snug group-hover:text-terracotta-soft transition-colors">
                    {e.title}
                  </h3>
                  <p className="mt-1.5 text-xs font-medium text-alabaster/60 tracking-wide">
                    {e.meta}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default Honors;
